import './StatusAndCompleteCell.scss';
import React, {useState, useContext} from "react";
import {Button} from "../../Form/Button/Button";
import {buttonColors} from "../../Pickers/InputTheme";
import {signDocToClassProgressValueConverter, formatCompletionDate} from "../../../Utils/BusinessLib";
import {isSignDocComplete, SignDocTypes} from "../../SignDoc/SignDocLib";
import {GridReactContext} from "../../../Utils/DataGridWrapper/GridContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {isEmpty, get} from "lodash";
import {SignDocCategories} from "../../../Utils/Constants";
import {OutcomeStatus} from "../../SignDoc/SignDocLib";

export const getUserTaskStatus = ({signDoc, i18n}) => {
    const signDocType = get(signDoc, 'signDoc.type', signDoc.type);
    const outcomeStatus = get(signDoc, 'outcomeStatus', null);
    const isCourse = signDocType === SignDocTypes.ONLINE_COURSE || signDocType === SignDocTypes.FRONTAL_COURSE;

    // For courses: check NO_SHOW status first
    if (isCourse && outcomeStatus === OutcomeStatus.NO_SHOW) {
        return i18n.translate("courseProgressMgmnt.noShow");
    }

    if (isSignDocComplete({
        type: signDocType,
        progressPercent: get(signDoc, 'progressPercent', signDoc.progressPercent),
        viewed: get(signDoc, 'viewed', signDoc.viewed),
        signed: get(signDoc, 'signed', signDoc.signed)})) {
        let statusText;

        // For courses: show "Attended" instead of "Finished"
        if (isCourse) {
            statusText = i18n.translate("courseProgressMgmnt.attended");
        } else if (signDocType === SignDocTypes.READ_AND_SIGN_DOC_PDF) {
            statusText = i18n.translate("deletedUserModal.status.signed");
        } else {
            statusText = i18n.translate("deletedUserModal.status.finished");
        }

        // Add exam score if it's an exam and has a score
        if (signDocType === SignDocTypes.EXAM && !isEmpty(signDoc.examScore)) {
            const score = parseInt(signDoc.examScore);
            if (score === 101) {
                // Admin completed - don't show score, will show admin text below
            } else {
                statusText += ` (${score})`;
            }
        }

        // Check if the signDoc was completed by an admin and append appropriate text
        if (signDoc.completedByUserId) {
            statusText += ` (${i18n.translate("deletedUserModal.signDocData.completedByAdmin")})`;
        }

        return statusText;
    } else if (signDoc.progressPercent > 0 || signDoc.viewed) {
        if (signDocType === SignDocTypes.LEARNING_PATH) {
            return `${signDoc.progressPercent}%`
        }
        // For UPLOAD_DOCUMENT, show "Waiting for approval" instead of "In Progress"
        if (signDocType === SignDocTypes.UPLOAD_DOCUMENT) {
            return i18n.translate("deletedUserModal.status.waitingForApproval")
        }
        // For courses, show "Registered" instead of "In Progress"
        if (isCourse) {
            return i18n.translate("courseProgressMgmnt.registered")
        }
        return i18n.translate("deletedUserModal.status.inProgress")
    } else {
        // For courses, show "Registered" for not started state too
        if (isCourse) {
            return i18n.translate("courseProgressMgmnt.registered")
        }
        return i18n.translate("deletedUserModal.status.notStarted")
    }
}

/**
 * Reusable component for displaying status with hover-based action buttons
 * Can be used in UserDetailsModal and OrganizationUsersSignDocsTable
 *
 * @param {object} params - The component parameters
 * @param {object} params.signDoc - The sign document object with progress data
 * @param {object} params.i18n - The internationalization context
 * @param {function} params.removeUserFromSignDoc - Function to remove user from sign doc
 * @param {function} params.markCompleted - Function to mark sign doc as completed
 * @param {function} params.unmarkCompleted - Function to unmark completion (admin only)
 * @param {boolean} params.isSignDocValid - Whether user is assigned to this sign doc
 * @param {function} params.onClickAddCourse - Function to add user to sign doc (when not valid)
 * @param {boolean} [params.readOnly] - When true, renders the status label only (no mutating actions). Used for ARCHIVED assignment rows.
 * @param {boolean} [params.showCompletionDate] - When true, shows the completion date under the status. Off by default,
 *                                                because tables that already carry a completion date column would duplicate it.
 * @returns {JSX.Element} The status and complete cell component
 */
export const StatusAndCompleteCell = ({
    signDoc,
    i18n,
    removeUserFromSignDoc,
    markCompleted,
    unmarkCompleted,
    isSignDocValid = true,
    onClickAddCourse,
    readOnly = false,
    showCompletionDate = false,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const gridContext = useContext(GridReactContext);

    const signDocType = get(signDoc, 'signDoc.type', signDoc.type);
    const isCourse = signDocType === SignDocTypes.ONLINE_COURSE || signDocType === SignDocTypes.FRONTAL_COURSE;
    // Rows whose grid id is not the SignDoc id (for example a learning path task, which must stay unique
    // in the grid even when the same SignDoc sits in two paths) carry the real id in signDocId
    const targetSignDocId = get(signDoc, 'signDocId') || get(signDoc, 'id');

    const isCompleted = isSignDocComplete({
        type: signDocType,
        progressPercent: get(signDoc, 'progressPercent', signDoc.progressPercent),
        viewed: get(signDoc, 'viewed', signDoc.viewed),
        signed: get(signDoc, 'signed', signDoc.signed)
    });
    const wasAdminCompleted = signDoc.completedByUserId;
    const statusText = getUserTaskStatus({signDoc, i18n});
    // Only a finished task has a meaningful date, so nothing is rendered for the other states
    const completionDateText = showCompletionDate && isCompleted
        ? formatCompletionDate({value: get(signDoc, 'completedOnDate', null)})
        : '';
    const progressClass = signDocToClassProgressValueConverter({
        signed: signDoc.signed,
        viewed: signDoc.viewed,
        progressPercent: signDoc.progressPercent,
        type: signDocType
    });
    
    const onMenuMouseEnter = () => {
        setIsHovered(true);
    };
    
    const onMenuMouseLeave = () => {
        setIsHovered(false);
    };
    
    const handleRemove = async () => {
        setIsLoading(true);
        try {
            await removeUserFromSignDoc(targetSignDocId);
            gridContext?.triggerTableRefresh();
        } catch (error) {
            console.error('Error removing user from sign doc:', error);
        }
        setIsLoading(false);
    };
    
    const handleMarkCompleted = async () => {
        setIsLoading(true);
        try {
            await markCompleted(targetSignDocId);
            gridContext?.triggerTableRefresh();
        } catch (error) {
            console.error('Error marking sign doc as completed:', error);
        }
        setIsLoading(false);
    };
    
    const handleUnmarkCompleted = async () => {
        setIsLoading(true);
        try {
            await unmarkCompleted(targetSignDocId);
            gridContext?.triggerTableRefresh();
        } catch (error) {
            console.error('Error unmarking sign doc as completed:', error);
        }
        setIsLoading(false);
    };
    
    const handleAddCourse = () => {
        if (onClickAddCourse) {
            onClickAddCourse();
        }
    };
    
    const getButtonsToShow = () => {
        const isCertification = signDoc.category === SignDocCategories.CERTIFICATION;

        if (!isSignDocValid) {
            return <Button color={buttonColors.GOLD}
                           text={i18n.translate('UsersPage.userRowSignDocMenu.buttons.addToSignDoc')}
                           onClick={handleAddCourse}/>;
        } else {
            return <div className={`cellButtonsWrapper ${isCompleted ? (wasAdminCompleted ? 'doubleVal' : 'singleVal') : ''}`}>
                {removeUserFromSignDoc && (
                    <Button color={buttonColors.LIGHT_GREY}
                            text={i18n.translate('UsersPage.userRowSignDocMenu.buttons.removeFromSignDoc')}
                            onClick={handleRemove}/>
                )}
                {/* Hide Mark Completed button for certifications */}
                {!isCertification && (
                    !isCompleted && markCompleted ?
                        <Button color={buttonColors.GOLD}
                                text={i18n.translate('UsersPage.userRowSignDocMenu.buttons.markCompleted')}
                                onClick={handleMarkCompleted}/>
                        :
                        (wasAdminCompleted && unmarkCompleted ?
                            <Button color={buttonColors.GOLD}
                                    text={i18n.translate('UsersPage.userRowSignDocMenu.buttons.unmarkCompleted')}
                                    onClick={handleUnmarkCompleted}/>
                            : '')
                )}
            </div>;
        }
    };
    
    // Courses and read-only (archived) rows: status label only, no hover actions
    if (isCourse || readOnly) {
        return (
            <div className="StatusAndCompleteCell">
                {isLoading ? <LoadingSpinner/> : '' }
                <div className="cellStatusProgress visible">
                    <div className={`progressText ${progressClass}`}>{statusText}</div>
                    {completionDateText && <div className="completionDate">{completionDateText}</div>}
                </div>
            </div>
        );
    }

    // For other SignDoc types: show hover actions
    return (
        <div className="StatusAndCompleteCell" onMouseEnter={onMenuMouseEnter} onMouseLeave={onMenuMouseLeave}>
            {isLoading ? <LoadingSpinner/> : '' }
            <div className={`cellStatusProgress ${isHovered ? 'hidden' : 'visible'}`}>
                <div className={`progressText ${progressClass}`}>{statusText}</div>
                {completionDateText && <div className="completionDate">{completionDateText}</div>}
            </div>
            <div className={`userRowButtons ${isHovered ? 'visible' : 'hidden'}`}>
                {getButtonsToShow()}
            </div>
        </div>
    );
};